import { createSlice } from '@reduxjs/toolkit'
import { settingsApi } from '../api/settings-api'

import slicesNotifications from './notifications'
import utilsFormatDate from '../utils/format-date'

const initialState = {
    regional_settings: {
        timezone: 'America/New_York',
        tz_today: null
    },
    status: {
        initialized: false,
        processing: false,
        has_error: false,
        error_message: null,

        is_document_visible: null,
        document_visible_change_timestamp: null,
        document_visible_prev_change_timestamp: null,
        utc_date: null,
        utc_timestamp: null,
        app_version: null,

        maintenance: false,
        maintenance_message: null,

        reload_page_needed: false,
        reload_page_reason: null
    },
    print: {
        is_printing: false
    },
    countries_support: {
        campaign_type: {
            audience_target: {
                list: [],
                defaults: []
            },
            geofence: {
                list: [],
                defaults: []
            }
        },
        countries: [],
        user_profiles: []
    },
    campaign: {
        status: {
            initialized: false,
            processing: false,
            has_error: false,
            error_message: null
        },

        common: {
            ad_formats: []
        },
        audience_target: {
            market_audiences: [],
            locations: [],
            geo_types: {
                states: [],
                districts: [],
                cities: [],
                counties: [],
                senates: [],
                houses: []
            }
        },
        geofence: {
            categories: [],
            system_locations: []
        }
    },
    validation: {
        zip: []
    },
    general: {
        env: 'prod'
    }
}

const slice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        changePrintingMode(state, action) {
            const { is_printing } = action.payload

            state.print = {
                ...state.print,
                is_printing: is_printing
            }
        },

        updateRegionalSettings(state, action) {
            const timezone = 'America/New_York'

            const new_tz_today = utilsFormatDate.getTzTodayDay(
                new Date(),
                timezone
            )
            const old_tz_today = state.regional_settings.tz_today

            state.regional_settings = {
                ...state.regional_settings,
                timezone,
                tz_today: new_tz_today
            }

            if (
                !state.status.reload_page_needed &&
                old_tz_today &&
                old_tz_today != new_tz_today
            ) {
                state.status = {
                    ...state.status,
                    reload_page_needed: true,
                    reload_page_reason: 'utc_date_changed'
                }
            }
        },
        changeDocumentVisibilityState(state, action) {
            const document_visible_prev_change_timestamp =
                state.status.document_visible_change_timestamp
            const document_visible_change_timestamp = new Date().getTime()

            const document_visible_sec_since_last_change =
                document_visible_change_timestamp
                    ? Math.floor(
                          (document_visible_change_timestamp -
                              document_visible_prev_change_timestamp) /
                              1000
                      )
                    : 0

            state.status = {
                ...state.status,
                is_document_visible: document.visibilityState == 'visible',
                document_visible_change_timestamp,
                document_visible_prev_change_timestamp,
                document_visible_sec_since_last_change
            }
        },

        updateStatus(state, action) {
            const {
                is_signed,
                app_version,
                maintenance,
                maintenance_message,
                utc_date,
                utc_timestamp
            } = action.payload

            let reload_page_needed = false
            let reload_page_reason = null

            if (!state.status.reload_page_needed) {
                if (
                    state.status.app_version &&
                    app_version &&
                    state.status.app_version != app_version
                ) {
                    reload_page_needed = true
                    reload_page_reason = 'app_version_changed'
                }
            }

            state.status = {
                ...state.status,
                utc_date,
                utc_timestamp,
                app_version,

                maintenance,
                maintenance_message,

                reload_page_needed: reload_page_needed,
                reload_page_reason: reload_page_reason
            }
        },
        initializeRequest(state, action) {
            state.status = {
                ...state.status,
                initialized: false,
                processing: true,
                has_error: false,
                error_message: null
            }
        },

        initializeFailed(state, action) {
            state.status = {
                ...state.status,
                initialized: false,
                processing: false,
                has_error: true,
                error_message: action.payload.errorMessage
            }
        },

        updateInitial(state, action) {
            const { countries_support, general } = action.payload

            state.status = {
                ...state.status,
                initialized: true,
                processing: false,
                has_error: false,
                error_message: null
            }

            state.general = {
                ...general
            }
            state.countries_support = {
                campaign_type: {
                    audience_target: {
                        list: [
                            ...countries_support.campaign_type.audience_target
                                .list
                        ],
                        defaults: [
                            ...countries_support.campaign_type.audience_target
                                .defaults
                        ]
                    },
                    geofence: {
                        list: [
                            ...countries_support.campaign_type.geofence.list
                        ],
                        defaults: [
                            ...countries_support.campaign_type.geofence.defaults
                        ]
                    }
                },
                countries: [...countries_support.countries],
                user_profiles: [...countries_support.user_profiles]
            }
        },

        initializeCampaignRequest(state, action) {
            state.campaign.status = {
                initialized: false,
                processing: true,
                has_error: false,
                error_message: null
            }
        },

        initializeCampaignFailed(state, action) {
            state.campaign.status = {
                initialized: false,
                processing: false,
                has_error: true,
                error_message: action.payload.errorMessage
            }
        },

        updateCampaign(state, action) {
            const { campaign, validation } = action.payload

            state.campaign = {
                status: {
                    initialized: true,
                    processing: false,
                    has_error: false,
                    error_message: null
                },
                ...campaign
            }
            state.validation = {
                ...validation
            }
        }
    }
})

export const { reducer } = slice

export const initializeEvent = () => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await settingsApi.initialize()

    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(
            slicesNotifications.addApiError(data, 'Loading Platform Settings')
        )
    } else {
        dispatch(slice.actions.updateInitial(data.body))
    }
}

export const loadByCountryCampaignTypeEvent =
    (country_code, campaign_type) => async (dispatch) => {
        dispatch(slice.actions.initializeCampaignRequest(data))

        const data = await settingsApi.byCountryCampaignType(
            country_code,
            campaign_type
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeCampaignFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Loading Platform Settings'
                )
            )
        } else {
            dispatch(slice.actions.updateCampaign(data.body))
        }
    }

export const loadStatusUpdate = () => async (dispatch) => {
    const data = await settingsApi.statusUpdate()

    if (data.statusCode == 200) {
        dispatch(slice.actions.updateStatus(data.body))
    }
}

export const updateRegionalSettings = () => async (dispatch) => {
    dispatch(slice.actions.updateRegionalSettings())
}

export const onDocumentVisibilityChange = () => async (dispatch) => {
    dispatch(slice.actions.changeDocumentVisibilityState())
}

export const changePrintingMode = (is_printing) => async (dispatch) => {
    dispatch(slice.actions.changePrintingMode({ is_printing }))
}
